/**
 * Plays sounds for select / dropdown / menu interactions site-wide.
 *
 * Reka UI (under @nuxt/ui) marks the trigger with `role="combobox"`,
 * each item with `role="option"` (selects) or `role="menuitem"`
 * (dropdowns). One delegated `pointerup` listener at document level
 * wires every USelect / USelectMenu / UDropdownMenu without per-page
 * code. Use `pointerup` not `pointerdown` because Reka's trigger
 * `preventDefault`s pointerdown which can interfere with downstream
 * delegation on some browsers.
 *
 * No-op until the user opts in via MSoundToggle.
 */

export default defineNuxtPlugin(() => {
  const { play } = useSound()

  function handle(e: Event) {
    const target = e.target as HTMLElement | null
    if (!target)
      return

    if (target.closest('[role="combobox"]')) {
      play('pop', { gain: 0.75, throttle: 80, rateJitter: 0.06 })
      return
    }
    if (target.closest('[role="option"]')) {
      play('toggle-on', { gain: 0.8, throttle: 80, rateJitter: 0.06 })
      return
    }
    if (target.closest('[role="menuitem"], [role="menuitemradio"], [role="menuitemcheckbox"]'))
      play('tick', { gain: 0.9, throttle: 60, rateJitter: 0.06 })
  }

  // pointerup catches mouse + touch + pen. Click is a fallback for
  // keyboard activations (Enter/Space) that don't fire pointer events.
  document.addEventListener('pointerup', handle, true)
  document.addEventListener('click', handle, true)
})
